import {
	Box,
	Button,
	Card,
	Chip,
	Collapse,
	Typography,
	Grid,
	TextField,
	FormControlLabel,
	Switch,
	InputAdornment,
} from '@mui/material';
import { LocalizationProvider } from '@mui/lab';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import DatePicker from '@mui/lab/DatePicker';
import { regexOnlyNumberInt } from '../helpers';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import React, { useEffect, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import moment from 'moment';
import { getDefaultOrigin } from '../services';
import '../../../assets/mevuelo-font/mv-fonts.css';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FilterListIcon from '@mui/icons-material/FilterList';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PublicIcon from '@mui/icons-material/Public';
import Autocomplete from '@mui/material/Autocomplete';
import notImage from '../../../assets/not-image.jpg';

// Importar sub-componentes y hooks
import { RoomConfigurationSection } from './QuotationsSimilarFilter/components/RoomConfigurationSection';
import { useRoomDistribution } from './QuotationsSimilarFilter/hooks/useRoomDistribution';
import { useDestinationSelection } from './QuotationsSimilarFilter/hooks/useDestinationSelection';

const useStyles = makeStyles((theme) => ({
	filterContainer: {
		marginBottom: theme.spacing(3),
	},
	filterCard: {
		borderRadius: theme.spacing(2),
		boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
		border: '1px solid rgba(0,0,0,0.05)',
		overflow: 'hidden',
		transition: 'all 0.3s ease',
	},
	filterHeader: {
		background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
		padding: theme.spacing(2, 3),
		color: 'white',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		cursor: 'pointer',
		transition: 'all 0.3s ease',
		'&:hover': {
			background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
		},
	},
	filterTitle: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1),
		fontWeight: 'bold',
		fontSize: '1.1rem',
	},
	filterContent: {
		padding: theme.spacing(3),
		backgroundColor: '#f8f9fa',
	},
	sectionCard: {
		backgroundColor: 'white',
		borderRadius: theme.spacing(1.5),
		padding: theme.spacing(2.5),
		marginBottom: theme.spacing(2),
		border: '1px solid rgba(0,0,0,0.05)',
		boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
	},
	sectionTitle: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1),
		marginBottom: theme.spacing(2),
		fontWeight: 'bold',
		color: theme.palette.primary.main,
		fontSize: '1rem',
	},
	inputField: {
		'& .MuiOutlinedInput-root': {
			borderRadius: theme.spacing(1.5),
			backgroundColor: '#f8f9fa',
			transition: 'all 0.3s ease',
			'&:hover': {
				backgroundColor: '#fff',
				boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
			},
			'&.Mui-focused': {
				backgroundColor: '#fff',
				boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
			},
		},
	},
	switchContainer: {
		backgroundColor: 'white',
		borderRadius: theme.spacing(1),
		padding: theme.spacing(1, 2),
		border: '1px solid rgba(0,0,0,0.05)',
		'& .MuiSwitch-switchBase.Mui-checked': {
			color: '#667eea',
		},
		'& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
			backgroundColor: '#667eea',
		},
	},
	destinationOption: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1),
		padding: theme.spacing(1),
		borderRadius: theme.spacing(1),
		transition: 'all 0.3s ease',
		'&:hover': {
			backgroundColor: '#f0f0f0',
		},
	},
	destinationImage: {
		width: '40px',
		height: '40px',
		borderRadius: '50%',
		objectFit: 'cover',
		border: '2px solid #ddd',
	},
	regionHeader: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(1),
		padding: theme.spacing(1, 2),
		backgroundColor: '#f5f5f5',
		borderRadius: theme.spacing(1),
		cursor: 'pointer',
		transition: 'all 0.3s ease',
		'&:hover': {
			backgroundColor: '#e0e0e0',
		},
	},
	actionButtons: {
		display: 'flex',
		gap: theme.spacing(2),
		marginTop: theme.spacing(3),
		justifyContent: 'center',
	},
	searchButton: {
		background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
		color: 'white',
		fontWeight: 'bold',
		padding: theme.spacing(1.5, 4),
		borderRadius: theme.spacing(3),
		fontSize: '1rem',
		minWidth: '140px',
		'&:hover': {
			background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
			transform: 'translateY(-2px)',
			boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
		},
		transition: 'all 0.3s ease',
	},
	clearButton: {
		background: 'transparent',
		color: theme.palette.text.secondary,
		border: `2px solid ${theme.palette.text.secondary}`,
		fontWeight: 'bold',
		padding: theme.spacing(1.5, 4),
		borderRadius: theme.spacing(3),
		fontSize: '1rem',
		minWidth: '140px',
		'&:hover': {
			backgroundColor: theme.palette.text.secondary,
			color: 'white',
			transform: 'translateY(-2px)',
		},
		transition: 'all 0.3s ease',
	},
}));

export interface QuotationRoomDistributionFilter {
	totalAdults?: number;
	totalChildren?: number | null;
	totalInfants?: number | null;
}

export interface QuotationFindSimilarFilter {
	onlyMyQuotations?: boolean;
	filterOnlyFlights?: boolean;
	wasCopied?: boolean;
	daysOfCreated?: number;
	origin?: string;
	destinationCodes?: string;
	travelFrom?: string;
	travelTo?: string;
	totalNights?: number;
	roomDistributions?: QuotationRoomDistributionFilter[];
}

export interface QuotationsSimilarFilterProps {
	setLoading: (loading: boolean) => void;
	onClearFilter: () => void;
	onApplyFilter: (filter: QuotationFindSimilarFilter) => void;
}

export const QuotationsSimilarFilter = (props: QuotationsSimilarFilterProps): JSX.Element => {
	const classes = useStyles();
	const [expanded, setExpanded] = useState<boolean>(false);
	const [filtersSimilar, setFiltersSimilar] = useState<QuotationFindSimilarFilter>({
		daysOfCreated: 5,
	});

	// Usar custom hooks
	const {
		roomDistributions,
		handleRoomNumberChange,
		handleAdultsChange,
		handleChildrenChange,
		handleInfantsChange,
		clearRooms,
	} = useRoomDistribution();

	const {
		regions,
		selectedDestinations,
		selectedRegions,
		handleDestinationsChange,
		handleRegionClick,
		clearDestinations,
		getDestinationCodes,
	} = useDestinationSelection();

	// Handlers simplificados
	const handleChangeOrigin = (event: React.ChangeEvent<HTMLInputElement>) => {
		setFiltersSimilar({ ...filtersSimilar, origin: event.target.value });
	};

	const handleChangeDaysOfCreated = (value?: number) => {
		setFiltersSimilar({ ...filtersSimilar, daysOfCreated: value });
	};

	const handleChangeTotalNights = (value?: number) => {
		setFiltersSimilar({ ...filtersSimilar, totalNights: value });
	};

	const handleChangeWasCopied = (event: React.ChangeEvent<HTMLInputElement>) => {
		setFiltersSimilar({ ...filtersSimilar, wasCopied: event.target.checked });
	};

	const handleChangeFilterOnlyFlights = (event: React.ChangeEvent<HTMLInputElement>) => {
		setFiltersSimilar({ ...filtersSimilar, filterOnlyFlights: event.target.checked });
	};

	const getTravelFrom = () => {
		return filtersSimilar.travelFrom ? moment(filtersSimilar.travelFrom).toDate() : null;
	};

	const getTravelTo = () => {
		return filtersSimilar.travelTo ? moment(filtersSimilar.travelTo).toDate() : null;
	};

	const handleChangeTravelFrom = (date: unknown) => {
		if (date) {
			setFiltersSimilar({
				...filtersSimilar,
				travelFrom: moment(date as Date).format('YYYY-MM-DD'),
			});
		} else {
			setFiltersSimilar({ ...filtersSimilar, travelFrom: undefined });
		}
	};

	const handleChangeTravelTo = (date: unknown) => {
		if (date) {
			setFiltersSimilar({
				...filtersSimilar,
				travelTo: moment(date as Date).format('YYYY-MM-DD'),
			});
		} else {
			setFiltersSimilar({ ...filtersSimilar, travelTo: undefined });
		}
	};

	useEffect(() => {
		const fetchOrigin = async () => {
			try {
				const originResponse = await getDefaultOrigin();
				setFiltersSimilar((prev) => ({ ...prev, origin: originResponse.data }));
			} catch (error) {
				console.error('Error fetching origin:', error);
			}
		};
		fetchOrigin();
	}, []);

	const clearFilters = () => {
		setFiltersSimilar({ daysOfCreated: 5 });
		clearRooms();
		clearDestinations();
		props.onClearFilter();
	};

	const handleApplyFilters = () => {
		const destinationCodes = getDestinationCodes();
		const filter: QuotationFindSimilarFilter = {
			...filtersSimilar,
			roomDistributions: roomDistributions.length > 0 ? roomDistributions : undefined,
			destinationCodes: destinationCodes.length > 0 ? destinationCodes.join(',') : undefined,
		};
		props.onApplyFilter(filter);
	};

	const hasActiveFilters = () => {
		return (
			filtersSimilar.origin ||
			selectedDestinations.length > 0 ||
			roomDistributions.length > 0 ||
			filtersSimilar.travelFrom ||
			filtersSimilar.travelTo ||
			filtersSimilar.totalNights ||
			filtersSimilar.daysOfCreated !== 5 ||
			filtersSimilar.wasCopied ||
			filtersSimilar.filterOnlyFlights
		);
	};

	return (
		<Box className={classes.filterContainer}>
			<Card className={classes.filterCard}>
				<Box className={classes.filterHeader} onClick={() => setExpanded(!expanded)}>
					<Box className={classes.filterTitle}>
						<FilterListIcon />
						<Typography>Filtros Avanzados de Búsqueda</Typography>
						{hasActiveFilters() && (
							<Chip
								size="small"
								label="Activos"
								sx={{
									backgroundColor: 'rgba(255,255,255,0.2)',
									color: 'white',
									fontWeight: 'bold',
								}}
							/>
						)}
					</Box>
					{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
				</Box>

				<Collapse in={expanded}>
					<Box className={classes.filterContent}>
						{/* Sección combinada de Origen, Destinos y Fechas */}
						<Card className={classes.sectionCard}>
							<Typography className={classes.sectionTitle}>
								<LocationOnIcon />
								Origen, Destinos y Fechas
							</Typography>
							<Grid container spacing={3}>
								<Grid item xs={12} md={6} lg={3}>
									<TextField
										fullWidth
										className={classes.inputField}
										value={filtersSimilar.origin || ''}
										onChange={handleChangeOrigin}
										label="Origen"
										variant="outlined"
										InputProps={{
											startAdornment: (
												<InputAdornment position="start">
													<FlightTakeoffIcon color="action" />
												</InputAdornment>
											),
										}}
									/>
								</Grid>
								<Grid item xs={12} md={6} lg={9}>
									<Autocomplete
										fullWidth
										className={classes.inputField}
										options={regions
											.map((region) => region.destinations)
											.reduce((accumulator, value) => accumulator.concat(value), [])}
										multiple={true}
										groupBy={(option) => option.regionName}
										getOptionLabel={(option) => option.name}
										clearOnBlur={false}
										getOptionDisabled={(option) => {
											return selectedRegions.indexOf(option.regionName) > -1;
										}}
										renderOption={(props, destination) => (
											<li {...props} key={destination.name} className={classes.destinationOption}>
												<img
													className={classes.destinationImage}
													src={destination?.image || notImage}
													alt={destination.name}
												/>
												<Typography>{destination.name}</Typography>
											</li>
										)}
										onChange={(event, value) => handleDestinationsChange(value)}
										value={selectedDestinations}
										renderGroup={(params) => {
											const region = regions.find((x) => x.name == params.group);
											if (region) {
												if (region.name == 'regionGroupSelect') {
													return <></>;
												}
												return (
													<Box key={'region-group' + params.group}>
														<Box
															className={classes.regionHeader}
															onClick={() => handleRegionClick(params.group)}
															sx={{
																backgroundColor: selectedRegions.indexOf(params.group) == -1 ? 'white' : '#e0e0e0',
															}}
														>
															{region?.icon ? (
																<i
																	style={{
																		height: '24px',
																		width: '24px',
																		fontSize: '20px',
																	}}
																	className={`icon-${region?.icon}`}
																></i>
															) : (
																<PublicIcon fontSize="small" />
															)}
															<Typography variant="subtitle2" fontWeight="bold">
																{region?.name || ''}
															</Typography>
														</Box>
														{params.children}
													</Box>
												);
											} else return <></>;
										}}
										renderInput={(params) => (
											<TextField {...params} label="Destinos" placeholder="Selecciona uno o más destinos" />
										)}
									/>
								</Grid>
								<Grid item xs={12} md={4}>
									<LocalizationProvider dateAdapter={AdapterDateFns}>
										<DatePicker
											inputFormat="dd/MM/yyyy"
											renderInput={(params) => (
												<TextField
													{...params}
													fullWidth
													className={classes.inputField}
													variant="outlined"
													helperText="Fecha de inicio"
												/>
											)}
											label="Fecha desde"
											value={getTravelFrom()}
											onChange={handleChangeTravelFrom}
											OpenPickerButtonProps={{
												'aria-label': 'cambiar fecha',
											}}
										/>
									</LocalizationProvider>
								</Grid>
								<Grid item xs={12} md={4}>
									<LocalizationProvider dateAdapter={AdapterDateFns}>
										<DatePicker
											inputFormat="dd/MM/yyyy"
											renderInput={(params) => (
												<TextField
													{...params}
													fullWidth
													className={classes.inputField}
													variant="outlined"
													helperText="Fecha de fin"
												/>
											)}
											label="Fecha hasta"
											value={getTravelTo()}
											onChange={handleChangeTravelTo}
											OpenPickerButtonProps={{
												'aria-label': 'cambiar fecha',
											}}
										/>
									</LocalizationProvider>
								</Grid>
								<Grid item xs={12} md={4}>
									<TextField
										fullWidth
										className={classes.inputField}
										variant="outlined"
										label="Cantidad de noches"
										type="number"
										placeholder="Ej: 7"
										onChange={({ target: { value } }) => {
											if (regexOnlyNumberInt.test(value) && Number(value) >= 1) {
												handleChangeTotalNights(Number(value));
											} else {
												handleChangeTotalNights();
											}
										}}
										value={filtersSimilar.totalNights || ''}
									/>
								</Grid>
							</Grid>
						</Card>

						{/* Sección de Configuración de Búsqueda */}
						<Card className={classes.sectionCard}>
							<Typography className={classes.sectionTitle}>
								<SearchIcon />
								Configuración de Búsqueda
							</Typography>
							<Grid container spacing={3}>
								<Grid item xs={12} md={6}>
									<TextField
										fullWidth
										className={classes.inputField}
										variant="outlined"
										label="Días de creada"
										type="number"
										placeholder="Ej: 5"
										helperText="Buscar cotizaciones creadas en los últimos N días"
										onChange={({ target: { value } }) => {
											if (regexOnlyNumberInt.test(value) && Number(value) >= 0) {
												handleChangeDaysOfCreated(Number(value));
											} else {
												handleChangeDaysOfCreated();
											}
										}}
										value={filtersSimilar.daysOfCreated || ''}
									/>
								</Grid>
								<Grid item xs={12} md={6}>
									<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
										<FormControlLabel
											className={classes.switchContainer}
											control={<Switch checked={filtersSimilar.wasCopied} onChange={handleChangeWasCopied} />}
											label="Cotizaciones copiadas"
										/>
										<FormControlLabel
											className={classes.switchContainer}
											control={
												<Switch checked={filtersSimilar.filterOnlyFlights} onChange={handleChangeFilterOnlyFlights} />
											}
											label="Solo cotizaciones de vuelos"
										/>
									</Box>
								</Grid>
							</Grid>
						</Card>

						{/* Sección de Habitaciones */}
						<RoomConfigurationSection
							roomDistributions={roomDistributions}
							onRoomNumberChange={handleRoomNumberChange}
							onAdultsChange={handleAdultsChange}
							onChildrenChange={handleChildrenChange}
							onInfantsChange={handleInfantsChange}
						/>

						{/* Botones de Acción */}
						<Box className={classes.actionButtons}>
							<Button
								className={classes.searchButton}
								variant="contained"
								size="large"
								startIcon={<SearchIcon />}
								onClick={handleApplyFilters}
							>
								Buscar Cotizaciones
							</Button>

							<Button
								className={classes.clearButton}
								variant="outlined"
								size="large"
								startIcon={<ClearIcon />}
								onClick={clearFilters}
							>
								Limpiar Filtros
							</Button>
						</Box>
					</Box>
				</Collapse>
			</Card>
		</Box>
	);
};
